import { useState } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";

import { functions } from "../FirebaseConfig";
import { useAuth } from "../lib/auth-context";

export interface ConflictDetails {
  kind: "flight-conflict";
  flights: {
    id: string;
    publicNumber: string | null;
    fecha: string | null;
    hora: string | null;
    originDisplay: string | null;
    destinationDisplay: string | null;
    requesterDisplayName: string | null;
    /** "flight" = direct time overlap; "ferry" = only the pre/post
     * ferry leg overlaps the proposed slot. */
    conflictKind?: "flight" | "ferry";
  }[];
  blackouts: {
    id: string;
    startAt: number;
    endAt: number;
    reason: string | null;
  }[];
}

export interface ConflictAttempt {
  fecha: string;
  hora: string;
  aircraftDisplayName: string;
  origin: string;
  destination: string;
}

function formatTsShort(ms: number): string {
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    day: "2-digit",
    month: "short",
  }).format(new Date(ms));
}

/**
 * Pops when submitFlight / updateFlight refuses because the slot is
 * taken. The body lists every conflicting flight + blackout, with a
 * pre-populated message the user can edit and send to the approver
 * admins. The user can't override the conflict from here — only the
 * message-to-admin path is available.
 */
export function ConflictModal({
  conflict,
  attempt,
  context,
  onClose,
}: {
  conflict: ConflictDetails;
  attempt: ConflictAttempt;
  /** "solicitar" or "editar"; tweaks the opening sentence. */
  context: "solicitar" | "editar";
  onClose: () => void;
}) {
  const { usuario } = useAuth();
  const isAdmin = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
  const summary = (() => {
    const lines: string[] = [];
    for (const f of conflict.flights) {
      const ferryTag = f.conflictKind === "ferry" ? " · traslado" : "";
      lines.push(
        `· Vuelo ${f.publicNumber ?? f.id}: ${f.fecha ?? ""} ${f.hora ?? ""}` +
          ` ${f.originDisplay ?? ""} → ${f.destinationDisplay ?? ""}` +
          (f.requesterDisplayName ? ` (${f.requesterDisplayName})` : "") +
          ferryTag
      );
    }
    for (const b of conflict.blackouts) {
      lines.push(
        `· Bloqueo del ${formatTsShort(b.startAt)} al ${formatTsShort(b.endAt)}` +
          (b.reason ? ` (${b.reason})` : "")
      );
    }
    return lines.join("\n");
  })();
  const intro =
    context === "editar"
      ? `Intenté mover un vuelo al ${attempt.fecha} a las ${attempt.hora} ` +
        `en ${attempt.aircraftDisplayName} de ${attempt.origin} a ${attempt.destination}, ` +
        `pero hay conflicto con:`
      : `Intenté solicitar un vuelo el ${attempt.fecha} a las ${attempt.hora} ` +
        `en ${attempt.aircraftDisplayName} de ${attempt.origin} a ${attempt.destination}, ` +
        `pero hay conflicto con:`;
  const [message, setMessage] = useState(
    `Hola,\n\n${intro}\n\n${summary}\n\n¿Pueden ayudarme a coordinar?`
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setError(null);
    setSending(true);
    try {
      const call = httpsCallable<{ message: string }, { ok: boolean }>(
        functions,
        "sendConflictInquiry"
      );
      await call({ message });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={() => (sending ? undefined : onClose())}
    >
      <div
        className="my-8 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold tracking-tight text-brand-900">
          Conflicto de horarios
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          {isAdmin
            ? "El avión ya tiene un vuelo o bloqueo en ese horario. Resuelve el conflicto (cancelar o mover) antes de continuar."
            : "El avión ya tiene un vuelo o bloqueo en ese horario, así que la solicitud no se puede enviar directamente. Puedes ajustar la fecha/hora o pedirle al admin que coordine."}
        </p>
        {isAdmin ? (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
            <div className="font-medium text-amber-900">Conflictos:</div>
            <ul className="mt-1 space-y-1">
              {conflict.flights.map((f) => (
                <li key={f.id}>
                  ·{" "}
                  <Link
                    to={`/admin/flights/${f.id}`}
                    onClick={onClose}
                    className="font-mono font-semibold underline hover:text-amber-700"
                  >
                    {f.publicNumber ?? f.id}
                  </Link>{" "}
                  — {f.fecha ?? ""} {f.hora ?? ""}{" "}
                  <span className="whitespace-nowrap">
                    {f.originDisplay ?? ""}
                  </span>{" "}
                  <span className="whitespace-nowrap">
                    → {f.destinationDisplay ?? ""}
                  </span>
                  {f.requesterDisplayName ? ` (${f.requesterDisplayName})` : ""}
                  {f.conflictKind === "ferry" ? (
                    <span className="ml-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase">
                      traslado
                    </span>
                  ) : null}
                </li>
              ))}
              {conflict.blackouts.map((b) => (
                <li key={b.id}>
                  · Bloqueo del {formatTsShort(b.startAt)} al{" "}
                  {formatTsShort(b.endAt)}
                  {b.reason ? ` (${b.reason})` : ""} —{" "}
                  <Link
                    to="/admin/aircraft"
                    onClick={onClose}
                    className="underline hover:text-amber-700"
                  >
                    administrar bloqueos
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
            <div className="font-medium text-slate-900">Conflictos:</div>
            <pre className="mt-1 whitespace-pre-wrap font-sans">{summary}</pre>
          </div>
        )}
        {!isAdmin && sent ? (
          <p className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            Mensaje enviado al admin. Te responderán por email.
          </p>
        ) : null}
        {!isAdmin && !sent ? (
          <>
            <label className="mt-4 block text-sm font-medium text-slate-700">
              Mensaje al admin (puedes editarlo)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              maxLength={2000}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
            {error ? (
              <p className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                {error}
              </p>
            ) : null}
          </>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={sending}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {isAdmin || sent ? "Cerrar" : "Ajustar"}
          </button>
          {!isAdmin && !sent ? (
            <button
              type="button"
              disabled={sending || !message.trim()}
              onClick={send}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
            >
              {sending ? "Enviando…" : "Enviar al admin"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
